/*
 * KundenCard.tsx - Informationen zu einem Kunden anzeigen.
 *
 * Created by Dr. Maximillian Dornseif 2022-03-01 in huWaWi3 26.0.3
 * Copyright (c) 2022 HUDORA GmbH
 */

import { LinkRender } from '../hd-link'
import { Adresse, LadeChecker } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import gql from 'graphql-tag'
import { useQuery } from 'urql'

import { KUNDE_FRAGMENT } from '../../fragments'

interface KundenCardProps {
  /** Kundennummer. Wenn leer oder kein Kunde gefunden, wird auch nichts angezeigt.
   * */
  nr: string
}

/** Anzeige von Informationen zu einer Kundennummer.
 *
 * Vor allem für die rechte Sidebar gedacht
 * */
export function KundenCard(props: KundenCardProps) {
  const [result] = useQuery({
    query: KUNDENCARD_QUERY,
    variables: { nr: props.nr },
  })
  const { data, fetching, error } = result
  const kunde = data?.entity

  if (!props.nr) {
    return null
  }

  return (
    <LadeChecker loading={fetching} error={error} data={kunde} label={`Lade Kunde`}>
      <Card sx={{ minWidth: '14em' }}>
        <CardContent>
          <section title="Kunden Info" className="kundeninfo">
            <Typography paragraph={false} sx={{ fontSize: '95%' }}>
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="stretch">
                <Stack>
                  <Typography paragraph={false} color="text.secondary" gutterBottom>
                    Kunde
                  </Typography>
                  <div>
                    <Typography>{kunde?.name}</Typography>{' '}
                    <span>
                      (<LinkRender value={kunde?.designator} />)
                    </span>
                  </div>
                  {kunde?.bezeichner ? <>{kunde?.bezeichner}</> : null}
                  <dl>
                    {kunde?.gruppennr ? (
                      <>
                        <dt>
                          <Typography paragraph={false} sx={{ fontSize: '95%' }} color="text.secondary">
                            Kundengruppe
                          </Typography>
                        </dt>
                        <dd>
                          {kunde?.gruppe?.name} (<LinkRender value={kunde?.gruppennr} />)
                        </dd>
                      </>
                    ) : null}
                    {kunde?.verbandsnr ? (
                      <>
                        <dt>
                          <Typography paragraph={false} sx={{ fontSize: '95%' }} color="text.secondary">
                            Verband
                          </Typography>
                        </dt>
                        <dd>
                          {kunde?.verband?.name} (<LinkRender value={kunde?.verbandsnr} />)
                        </dd>
                      </>
                    ) : null}
                    {kunde?.kundenrabatt_prozent > 0 ? (
                      <>
                        <dt>
                          <Typography paragraph={false} sx={{ fontSize: '95%' }} color="text.secondary">
                            Kundenrabatt / Zahlungskondition
                          </Typography>
                        </dt>
                        <dd>
                          {kunde?.kundenrabatt_prozent} {kunde?.kundenrabatt_test} {' % / '}
                          {kunde?.skontotage} {' Tage '}
                          {kunde?.skontoprozent} %, {kunde?.nettotage} {' Tage '} netto
                        </dd>
                      </>
                    ) : null}

                    {kunde?.kreditlimit ? (
                      <>
                        <dt>
                          <Typography paragraph={false} sx={{ fontSize: '95%' }} color="text.secondary">
                            Kreditlimit
                          </Typography>
                        </dt>
                        <dd>
                          <ValueRender value={kunde?.kreditlimit} format="eurocentlarge" />
                        </dd>
                      </>
                    ) : null}
                  </dl>
                </Stack>
                <Stack direction="column" justifyContent="space-between" alignItems="flex-start">
                  <Adresse value={kunde?.adresse} />

                  {kunde?.zuteilungssperre ? (
                    <div>
                      <Chip label="Zuteilungssperre" size="small" color="warning" />
                    </div>
                  ) : null}
                </Stack>
              </Stack>
            </Typography>
          </section>
        </CardContent>
      </Card>
    </LadeChecker>
  )
}

const KUNDENCARD_QUERY = gql`
  query KundenCard($nr: ID!) {
    entity: hwKunde(designator: $nr) {
      ...kundeFragment
      gruppe {
        id
        name
      }
      verband {
        id
        name
      }
    }
  }
  ${KUNDE_FRAGMENT}
`
